import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [systemPreference, setSystemPreference] = useState('light');

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPreference(mediaQuery.matches ? 'dark' : 'light');

    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Initialize theme from localStorage. This app only has a light theme
  // implemented (see index.html's color-scheme meta tag comment) — no page
  // opts into 'dark' via this context, so we no longer default new visitors
  // to 'system'. Auto-following prefers-color-scheme silently turned on
  // scattered, incomplete dark: Tailwind classes and made text unreadable
  // (e.g. the Lead Stage dropdown). A saved 'dark'/'system' choice from
  // before this fix is still honored so nothing breaks for anyone who set it.
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      setTheme(savedTheme);
    } else {
      setTheme('light');
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    const effectiveTheme = theme === 'system' ? systemPreference : theme;
    
    // Remove existing theme classes
    document.documentElement.classList.remove('light', 'dark');
    
    // Add current theme class
    document.documentElement.classList.add(effectiveTheme);
    
    // Set CSS custom property for theme
    document.documentElement.style.setProperty('--theme', effectiveTheme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        effectiveTheme === 'dark' ? '#1a1a1a' : '#ffffff'
      );
    }
  }, [theme, systemPreference]);

  // Theme switching functions
  const setLightTheme = () => {
    setTheme('light');
    localStorage.setItem('theme', 'light');
  };

  const setDarkTheme = () => {
    setTheme('dark');
    localStorage.setItem('theme', 'dark');
  };

  const setSystemTheme = () => {
    setTheme('system');
    localStorage.setItem('theme', 'system');
  };

  const toggleTheme = () => {
    const effectiveTheme = theme === 'system' ? systemPreference : theme;
    if (effectiveTheme === 'light') {
      setDarkTheme();
    } else {
      setLightTheme();
    }
  };

  // Get effective theme (resolves 'system' to actual theme)
  const getEffectiveTheme = () => {
    return theme === 'system' ? systemPreference : theme;
  };

  // Check if current theme is dark
  const isDark = () => {
    return getEffectiveTheme() === 'dark';
  };

  // Check if current theme is light
  const isLight = () => {
    return getEffectiveTheme() === 'light';
  };

  // Check if using system theme
  const isSystemTheme = () => {
    return theme === 'system';
  };

  // Get theme icon
  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return '☀️';
      case 'dark':
        return '🌙';
      case 'system':
        return '💻';
      default:
        return '☀️';
    }
  };

  // Get theme display name
  const getThemeDisplayName = () => {
    switch (theme) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'system':
        return 'System';
      default:
        return 'Light';
    }
  };

  // Theme colors (can be used for dynamic styling)
  const themeColors = {
    light: {
      primary: '#3b82f6',
      secondary: '#64748b',
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#1e293b',
      textSecondary: '#64748b',
      border: '#e2e8f0',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
    dark: {
      primary: '#60a5fa',
      secondary: '#94a3b8',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#f1f5f9',
      textSecondary: '#94a3b8',
      border: '#334155',
      success: '#34d399',
      warning: '#fbbf24',
      error: '#f87171',
      info: '#60a5fa',
    },
  };

  // Get current theme colors
  const getCurrentThemeColors = () => {
    return themeColors[getEffectiveTheme()];
  };

  // CSS variables for theme
  const getCSSVariables = () => {
    const colors = getCurrentThemeColors();
    return {
      '--color-primary': colors.primary,
      '--color-secondary': colors.secondary,
      '--color-background': colors.background,
      '--color-surface': colors.surface,
      '--color-text': colors.text,
      '--color-text-secondary': colors.textSecondary,
      '--color-border': colors.border,
      '--color-success': colors.success,
      '--color-warning': colors.warning,
      '--color-error': colors.error,
      '--color-info': colors.info,
    };
  };

  // Apply CSS variables to document
  useEffect(() => {
    const variables = getCSSVariables();
    Object.entries(variables).forEach(([property, value]) => {
      document.documentElement.style.setProperty(property, value);
    });
  }, [theme, systemPreference]);

  const value = {
    // Current theme state
    theme,
    systemPreference,
    effectiveTheme: getEffectiveTheme(),
    
    // Theme setters
    setTheme,
    setLightTheme,
    setDarkTheme,
    setSystemTheme,
    toggleTheme,
    
    // Theme checkers
    isDark,
    isLight,
    isSystemTheme,
    
    // Theme info
    getThemeIcon,
    getThemeDisplayName,
    getCurrentThemeColors,
    getCSSVariables,
    
    // Theme colors
    colors: getCurrentThemeColors(),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;